import {
  LayoutDashboard,
  ClipboardList,
  PlusCircle,
  Wallet,
  Inbox,
  Truck,
  FileCheck2,
  Users,
  Receipt,
  CreditCard,
  Bell,
  DollarSign,
  Coins,
  Settings,
  ShieldCheck,
  Car,
  MapPin,
  Route,
  Hourglass,
  Percent,
  UserPlus,
  UserCog,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import type { Role } from "../../../../shared/const";

export type NavItem = {
  to: string;
  icon: LucideIcon;
  label: Record<"zh-TW" | "en-US" | "zh-CN", string>;
};

export type NavGroup = {
  groupLabel: Record<"zh-TW" | "en-US" | "zh-CN", string>;
  items: NavItem[];
};

const T = (zh: string, en: string, cn?: string) => ({ "zh-TW": zh, "en-US": en, "zh-CN": cn ?? zh });

/** 基礎資料（20260611 反饋批次）— dispatcher 與 admin 平台共管；agency / fleet 不可見 */
const BASE_DATA_GROUP: NavGroup = {
  groupLabel: T("基礎資料", "Base data", "基础资料"),
  items: [
    { to: "/vehicle-models", icon: Car, label: T("車款管理", "Vehicle models", "车款管理") },
    { to: "/pois", icon: MapPin, label: T("POI 與飯店", "POIs & hotels", "POI 与饭店") },
    { to: "/route-prices", icon: Route, label: T("路線價格", "Route prices", "路线价格") },
    { to: "/charter-plans", icon: Hourglass, label: T("包車方案", "Charter plans", "包车方案") },
    // D10：一律 HKD、不維護匯率表 → 匯率表入口退役（頁面與路由保留，僅隱藏導覽）
    { to: "/commission-rules", icon: Percent, label: T("拆帳設定", "Commission", "拆帐设定") },
  ],
};

export const NAV_BY_ROLE: Record<Role, NavGroup[]> = {
  agency: [
    {
      groupLabel: T("總覽", "Overview"),
      items: [
        { to: "/", icon: LayoutDashboard, label: T("工作台", "Dashboard", "工作台") },
      ],
    },
    {
      groupLabel: T("訂單", "Orders"),
      items: [
        { to: "/orders/new", icon: PlusCircle, label: T("建立用車需求", "New request", "建立用车需求") },
        { to: "/orders", icon: ClipboardList, label: T("訂單追蹤", "My orders", "订单追踪") },
      ],
    },
    {
      // A18：中文「帳務」改「帳單管理」、英文 BILLING 改 Account Management (Monthly Statement)
      groupLabel: T("帳單管理", "Account Management (Monthly Statement)", "账单管理"),
      items: [
        { to: "/settlements", icon: Wallet, label: T("每月訂單", "Monthly Orders", "每月订单") },
      ],
    },
    {
      groupLabel: T("通知", "Notifications", "通知"),
      items: [
        { to: "/notifications", icon: Bell, label: T("通知中心", "Notifications", "通知中心") },
      ],
    },
  ],

  fleet: [
    {
      groupLabel: T("總覽", "Overview"),
      items: [
        { to: "/", icon: LayoutDashboard, label: T("工作台", "Dashboard", "工作台") },
      ],
    },
    {
      groupLabel: T("派遣", "Dispatch"),
      items: [
        { to: "/dispatch-inbox", icon: Inbox, label: T("待確認派遣", "Pending", "待确认派遣") },
        { to: "/orders", icon: Truck, label: T("接單紀錄", "My jobs", "接单纪录") },
        // 2026-08-26：常用司機名冊
        { to: "/drivers", icon: UserRound, label: T("常用司機", "My drivers", "常用司机") },
      ],
    },
    {
      groupLabel: T("帳務", "Billing"),
      items: [
        { to: "/settlements", icon: Wallet, label: T("應收帳款", "Receivables", "应收账款") },
      ],
    },
    {
      groupLabel: T("通知", "Notifications", "通知"),
      items: [
        { to: "/notifications", icon: Bell, label: T("通知中心", "Notifications", "通知中心") },
      ],
    },
  ],

  dispatcher: [
    {
      groupLabel: T("總覽", "Overview"),
      items: [
        { to: "/", icon: LayoutDashboard, label: T("工作台", "Dashboard", "工作台") },
      ],
    },
    {
      groupLabel: T("營運", "Operations"),
      items: [
        { to: "/orders", icon: ClipboardList, label: T("訂單管理", "Orders", "订单管理") },
        { to: "/quoting", icon: FileCheck2, label: T("報價作業", "Quoting", "报价作业") },
        { to: "/orders/new-for-agency", icon: UserPlus, label: T("人工報價", "Manual quote", "人工报价") },
        { to: "/dispatch", icon: Truck, label: T("派遣作業", "Dispatch", "派遣作业") },
        { to: "/fleet-costs", icon: Coins, label: T("車費成本", "Fleet costs", "车费成本") },
      ],
    },
    {
      groupLabel: T("合作方", "Parties"),
      items: [
        { to: "/parties", icon: Users, label: T("旅行社 / 車隊", "Agencies / Fleets", "旅行社 / 车队") },
        // 2026-08-26：調度員可建立／維護需求方與供應方帳號
        { to: "/users", icon: UserCog, label: T("帳號管理", "Accounts", "帐号管理") },
      ],
    },
    BASE_DATA_GROUP,
    {
      groupLabel: T("帳務 & 金流", "Billing & Payments"),
      items: [
        { to: "/settlements", icon: Receipt, label: T("帳務結算", "Settlements", "账务结算") },
        { to: "/payments", icon: CreditCard, label: T("金流管理", "Payments", "金流管理") },
      ],
    },
    {
      groupLabel: T("通知", "Notifications"),
      items: [
        { to: "/notifications", icon: Bell, label: T("通知中心", "Notifications", "通知中心") },
      ],
    },
  ],

  admin: [
    {
      groupLabel: T("總覽", "Overview"),
      items: [
        { to: "/", icon: LayoutDashboard, label: T("工作台", "Dashboard", "工作台") },
      ],
    },
    {
      groupLabel: T("營運（觀察）", "Operations (read-only)"),
      items: [
        { to: "/orders", icon: ClipboardList, label: T("訂單總覽", "All orders", "订单总览") },
        { to: "/settlements", icon: Receipt, label: T("結算總覽", "All settlements", "结算总览") },
      ],
    },
    BASE_DATA_GROUP,
    {
      groupLabel: T("設定", "Settings"),
      items: [
        { to: "/pricing", icon: DollarSign, label: T("計價規則", "Pricing rules", "计价规则") },
        { to: "/fleet-costs", icon: Coins, label: T("車費成本", "Fleet costs", "车费成本") },
        { to: "/parties", icon: Users, label: T("合作方管理", "Parties", "合作方管理") },
        { to: "/users", icon: UserCog, label: T("帳號管理", "Accounts", "帐号管理") },
        { to: "/settings", icon: Settings, label: T("系統參數", "System", "系统参数") },
        { to: "/audit-logs", icon: ShieldCheck, label: T("操作日誌", "Audit logs", "操作日志") },
      ],
    },
    {
      groupLabel: T("通知", "Notifications", "通知"),
      items: [
        { to: "/notifications", icon: Bell, label: T("通知中心", "Notifications", "通知中心") },
      ],
    },
  ],
};
